import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ReportPreviewPayload } from 'api/analytics-reports/types';
import { countPreviewPagesForProfile } from 'app/modules/analytics/modules/reports/utils/countPreviewPagesForProfile';
import ViewReportPreviewProfileCard from './ViewReportPreviewProfileCard';
import type { ReportPreviewLayout } from 'app/modules/analytics/modules/reports/constants/reportPreviewPageShellClassName';

interface Props {
  preview: ReportPreviewPayload;
  layout?: ReportPreviewLayout;
  breakBeforeFirstPage?: boolean;
  pageOffset?: number;
}

const ViewReportPreviewContent = ({
  preview,
  layout = 'modal',
  breakBeforeFirstPage = false,
  pageOffset = 0,
}: Props) => {
  const { t } = useTranslation();

  const sortedProfiles = useMemo(
    () =>
      [...preview.profiles].sort(
        (left, right) => left.order_column - right.order_column,
      ),
    [preview.profiles],
  );

  const profilePageCounts = useMemo(
    () =>
      sortedProfiles.map((profile) => countPreviewPagesForProfile(profile, t)),
    [sortedProfiles, t],
  );

  const totalContentPages = useMemo(
    () => profilePageCounts.reduce((sum, count) => sum + count, 0),
    [profilePageCounts],
  );

  const totalPages = totalContentPages + pageOffset;

  const profileStartPages = useMemo(() => {
    let offset = pageOffset;
    return profilePageCounts.map((count) => {
      const start = offset + 1;
      offset += count;
      return start;
    });
  }, [profilePageCounts, pageOffset]);

  if (sortedProfiles.length === 0 || totalContentPages === 0) {
    return (
      <p className="tw-py-8 tw-text-center tw-text-sm tw-text-grayscale-120">
        {t('analyticsReports.viewModal.noMetricsConfigured')}
      </p>
    );
  }

  return (
    <div
      className={
        layout === 'export'
          ? 'tw-w-full'
          : 'tw-flex tw-min-h-full tw-w-full tw-flex-col tw-items-center tw-gap-8'
      }
    >
      {sortedProfiles.map((profile, index) => {
        if (profilePageCounts[index] === 0) {
          return null;
        }

        return (
          <ViewReportPreviewProfileCard
            key={profile.id}
            profile={profile}
            report={preview.report}
            startPageNumber={profileStartPages[index]}
            totalPages={totalPages}
            layout={layout}
            breakBeforeFirstPage={breakBeforeFirstPage}
          />
        );
      })}
    </div>
  );
};

export default ViewReportPreviewContent;
